import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Avatar, Box, Button, Chip, Divider, Typography, Skeleton } from "@mui/material";

import { formatLocalizedTimeAgo } from "../../../../../../utils/formatDate";
import { getServiceRequestPageRoute } from "../../../../../../routers/routeUtils";

import { ServiceRequest } from "../../../../types/service.type";
import { useRequestUserData } from "../../../../hooks/useRequestUserData";
import { useServiceStatusFlags } from "../../../../hooks/useServiceStatusFlags";
import { useServiceRequestActions } from "../../../../hooks/useServiceRequestActions";
import { CancelModal, useCancelModal } from "../../../../components/CancelModal";

export const ServiceRequestCard = ({ request }: { request: ServiceRequest }) => {
  const { t, i18n } = useTranslation("goService", { keyPrefix: "myRequestsPage" });
  const { t: tServiceRequestStatus } = useTranslation("goService", { keyPrefix: "serviceRequestStatus" });

  const { service, status, createdAt } = request;
  const { media, firstTwoChr, fullName, role, profileLink } = useRequestUserData(request);
  const { name } = service;

  const { isProvider, hasContract, isCompleted, isCanceled, isRejected, isInitiated, isNegotiated, isContracted } =
    useServiceStatusFlags(request);

  const {
    handleAcceptContract,
    handleEndContract,
    handleProviderCancel,
    handleRequestService,
    handleWithdrawRequest,
    handleCreateContract,
    handleNegotiateContract,
    isResponding,
    isRequesting,
    isCancellingByProvider,
    isWithdrawing,
  } = useServiceRequestActions(request);

  const { openCancelModal, handleOpenCancelModal, handleCloseCancelModal } = useCancelModal();

  const handleEndContractFromModal = () => {
    handleEndContract();
    handleCloseCancelModal();
  };

  const handleWithdrawRequestFromModal = () => {
    handleWithdrawRequest();
    handleCloseCancelModal();
  };

  const getActionButtons = () => {
    // Provider logic
    if (isProvider) {
      if (isRejected || isCanceled || isCompleted) return null;

      if (!hasContract) {
        return (
          <Button variant="outlined-sm" onClick={handleProviderCancel} disabled={isCancellingByProvider}>
            {/* {t("rejectService")} */}
            {t("rejectRequest")}
          </Button>
        );
      }

      if (isContracted) {
        return (
          <>
            <Button variant="outlined-sm" onClick={handleOpenCancelModal} disabled={isResponding}>
              {t("endContract")}
            </Button>
            <CancelModal
              open={openCancelModal}
              closeModal={handleCloseCancelModal}
              handelCancel={handleEndContractFromModal}
              title={t("areYouSureToRejectThisContract")}
              subTitle={t("onceRejectedThisContractThisActionCannotBeUndone")}
            />
          </>
        );
      }

      if (isNegotiated) {
        return (
          <Box sx={styles.actionsContainer}>
            <Button variant="primary-sm" onClick={handleAcceptContract} disabled={isResponding}>
              {t("acceptContract")}
            </Button>
            <Button variant="outlined-sm" onClick={handleOpenCancelModal} disabled={isResponding}>
              {t("endContract")}
            </Button>
            <CancelModal
              open={openCancelModal}
              closeModal={handleCloseCancelModal}
              handelCancel={handleEndContractFromModal}
              title={t("areYouSureToRejectThisContract")}
              subTitle={t("onceRejectedThisContractThisActionCannotBeUndone")}
            />
          </Box>
        );
      }

      return null;
    }

    // Requester logic
    if (isNegotiated) {
      return (
        <Button variant="primary-sm" onClick={handleNegotiateContract}>
          {t("negotiateContract", "Negotiate Contract")}
        </Button>
      );
    }

    if (isInitiated) {
      return (
        <Box sx={styles.actionsContainer}>
          <Button variant="primary-sm" onClick={handleCreateContract}>
            {t("createContract")}
          </Button>
          <Button variant="outlined-sm" onClick={handleOpenCancelModal} disabled={isResponding}>
            {t("withdrawRequest")}
          </Button>
          <CancelModal
            open={openCancelModal}
            closeModal={handleCloseCancelModal}
            handelCancel={handleWithdrawRequestFromModal}
            title={t("areYouSureToWithdrawThisRequest")}
          />
        </Box>
      );
    }

    if (isRejected || isCanceled || isCompleted) {
      return (
        <Button variant="primary-sm" onClick={handleRequestService} disabled={isRequesting}>
          {t("requestThisService")}
        </Button>
      );
    }

    return null;
  };

  const actions = getActionButtons();

  return (
    <Box sx={styles.container}>
      <Box sx={styles.serviceNameContainer}>
        <Typography sx={styles.serviceName} component={Link} to={getServiceRequestPageRoute(request.id)}>
          {name}
        </Typography>
        <Chip label={tServiceRequestStatus(status)} sx={styles.statusChip(status)} />
      </Box>
      <Divider sx={styles.divider} />
      <Box sx={styles.serviceProviderContainer}>
        <Avatar sx={styles.serviceProviderAvatar} src={media}>
          <Typography>{firstTwoChr}</Typography>
        </Avatar>
        <Box>
          <Typography component={Link} to={profileLink} sx={styles.serviceProviderName}>
            {fullName}
          </Typography>
          <Typography sx={styles.serviceProviderRole}>{role}</Typography>
        </Box>
      </Box>
      <Divider sx={styles.divider} />
      <Box sx={styles.serviceDetailsContainer}>
        <Typography sx={styles.serviceDetailsTime}>{formatLocalizedTimeAgo(createdAt, i18n.language)}</Typography>
        {actions}
      </Box>
    </Box>
  );
};

export const ServiceRequestCardLoader = () => {
  return (
    <Box sx={styles.container}>
      <Box sx={styles.serviceNameContainer}>
        <Skeleton variant="text" width={200} height={24} />
        <Skeleton variant="rounded" width={80} height={22} sx={{ borderRadius: "16px" }} />
      </Box>
      <Divider sx={styles.divider} />
      <Box sx={styles.serviceProviderContainer}>
        <Skeleton variant="circular" sx={styles.serviceProviderAvatar} />
        <Box>
          <Skeleton variant="text" width={150} height={20} />
          <Skeleton variant="text" width={120} height={20} />
        </Box>
      </Box>
      <Divider sx={styles.divider} />
      <Box sx={styles.serviceDetailsContainer}>
        <Skeleton variant="text" width={100} height={20} />
        <Skeleton variant="rounded" width={100} height={36} sx={{ borderRadius: "8px" }} />
      </Box>
    </Box>
  );
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "16px",
    borderRadius: "8px",
    border: "1px solid #E7E7E7",
  },
  serviceNameContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  serviceName: {
    color: "var(--Gray-900, #101828)",
    fontFamily: "Inter",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
    textDecoration: "none",
  },
  statusChip: (status: string) => ({
    padding: "2px 8px",
    borderRadius: "16px",
    border: "1px solid",
    background: "#FEF0E6",
    fontFamily: "Inter",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",
    height: "fit-content",
    ...styles.statusStyles(status),
    "& .MuiChip-label": {
      padding: 0,
    },
  }),
  statusStyles: (status: string) => {
    if (status === "rejected") {
      return {
        color: "#EF4444",
        backgroundColor: "#FDE8E8",
        borderColor: "#F9B9B9",
      };
    }
    if (status === "initiated") {
      return {
        color: "var(--Blue-700, #175CD3)",
        backgroundColor: "var(--Blue-50, #EFF8FF)",
        borderColor: "var(--Blue-200, #B2DDFF)",
      };
    }
    if (status === "negotiated") {
      return {
        color: "#F97316",
        backgroundColor: "#FEF0E6",
        borderColor: "#FCD3B5",
      };
    }
    if (status === "contracted") {
      return {
        color: "var(--Blue-700, #175CD3)",
        backgroundColor: "#FEF0E6",
        borderColor: "#FCD3B5",
      };
    }
    if (status === "completed") {
      return {
        color: "#076D0D",
        backgroundColor: "#E7FDE8",
        borderColor: "#7EF785",
      };
    }
    if (status === "canceled") {
      return {
        color: "#344054",
        backgroundColor: "#EFF1F5",
        borderColor: "#D0D6E2",
      };
    }
    return {};
  },
  divider: {
    mx: "-16px",
  },
  serviceProviderContainer: {
    display: "flex",
    gap: "12px",
  },
  serviceProviderAvatar: {
    width: "40px",
    height: "40px",
  },
  serviceProviderName: {
    color: "var(--Gray-900, #101828)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "20px",
    textDecoration: "none",
  },
  serviceProviderRole: {
    color: "var(--Gray-600, #475467)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "20px",
  },
  serviceDetailsContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  serviceDetailsTime: {
    color: "var(--Gray-600, #475467)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  actionsContainer: {
    display: "flex",
    gap: "8px",
  },
};
